import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";

interface Props {
  ga4MeasurementId?: string;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * GA4 route-change reporting.
 *
 * The GA4 script + config live in the SSR root head, so the first page load is
 * counted server-rendered. For client-side navigations GA4's enhanced
 * measurement ("page changes based on browser history events") already emits a
 * page_view — verified live: each SPA navigation produces its own
 * /g/collect?en=page_view hit. Sending our own gtag('event','page_view') on top
 * of that double-counted every navigation, which is why session/pageview
 * numbers looked inflated and erratic.
 *
 * So we deliberately do NOT send a manual page_view here. This component only
 * verifies the tag is present and warns (dev only) when it is missing, so a
 * regression in the root head is visible instead of silent.
 */
export function DeferredHeadAssets({ ga4MeasurementId }: Props) {
  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined" || !ga4MeasurementId) return;
    if (!import.meta.env.DEV) return;

    const unsub = router.subscribe("onResolved", () => {
      if (typeof window.gtag !== "function") {
        console.warn("[analytics] gtag missing on route change — GA4 tag not loaded");
      }
    });
    return () => unsub();
  }, [router, ga4MeasurementId]);

  return null;
}


